import { useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Radio, X } from 'lucide-react';
import { useSocket } from '../../../../context/SocketContext';
import { getTrainerDirectory } from '../../../../services/TrainerDirectoryService';
import { useWaypoint } from '../waypoint/WaypointProvider.jsx';
import '../waypoint/waypoint.css';
import './online-students.css';

/* =============================================================
   Online trainers, live.

   The trainer half of OnlineStudentsBox, and deliberately the same
   figure and the same popup -- see that file's header for why presence
   is read from the socket rather than asked of the server: the count
   moves the moment someone connects or drops, and a total baked into
   the overview payload would be stale before it was drawn.

   Who counts is a much shorter question here than it is for students.
   SocketContext's `onlineUsers` holds AUTH user ids, and a trainer row
   carries that same id as `userId` (trainer.schema.prisma -- it is the
   only link between the two databases). So the whole join is the
   trainer directory read once, keyed by userId, intersected with
   presence. No role lookup, and no resolving ids one burst at a time
   the way the admin student path has to.

   /trainerdirectory rather than /trainers: TrainerService.list()
   row-scopes a non-superadmin caller down to their OWN trainer record,
   which would make this tile read 1 for anyone below an admin. The
   directory is the unscoped roster and already returns userId.

   The figure opens the list, and every name opens the trainer through
   Waypoint. No second lookup on the way: /trainers/trainer/:id matches
   on userId first and falls back to the record id, so the id presence
   gave us is one Waypoint takes as-is.
   ============================================================= */

const EMPTY = new Set();

const trainerName = (t) =>
  t?.trainerName || t?.name || t?.trainerEmail || 'Unnamed trainer';

const OnlineTrainersBox = () => {
  const socketCtx = useSocket();
  const onlineUsers = socketCtx?.onlineUsers || EMPTY;
  const connected = Boolean(socketCtx?.isConnected);
  const { open: openEntity } = useWaypoint();
  const titleId = useId();

  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showList, setShowList] = useState(false);
  // auth userId -> the row to show
  const [directory, setDirectory] = useState(() => new Map());

  // Who counts: loaded once.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const rows = await getTrainerDirectory();
        const map = new Map();
        (Array.isArray(rows) ? rows : []).forEach((t) => {
          const uid = String(t?.userId || '').trim();
          // A trainer with no account behind them can never be online.
          if (!uid || uid === 'NA') return;
          map.set(uid, {
            key: uid,
            name: trainerName(t),
            specialization: t.specialization || '',
          });
        });
        if (alive) setDirectory(map);
      } catch (error) {
        console.error('Online trainers: could not load the directory', error);
        if (alive) setFailed(true);
      } finally {
        if (alive) setReady(true);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, []);

  const trainers = useMemo(
    () =>
      [...onlineUsers]
        .map((id) => directory.get(String(id)))
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [onlineUsers, directory],
  );

  useEffect(() => {
    if (!showList) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowList(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showList]);

  const openTrainer = (row) => {
    // A Waypoint opened from inside a popup closes the popup first.
    setShowList(false);
    openEntity('trainer', row.key);
  };

  const unavailable = ready && failed;
  const count = !ready || unavailable ? '—' : trainers.length;
  // A zero stays a plain figure rather than opening an empty list.
  const canOpen = ready && !failed && trainers.length > 0;
  const Tag = canOpen ? 'button' : 'div';
  const tagProps = canOpen
    ? { type: 'button', onClick: () => setShowList(true), title: 'Show who is online' }
    : {};
  const dot = (
    <i className={`onl-dot${connected && !unavailable ? '' : ' onl-dot--off'}`} aria-hidden="true" />
  );
  const status = unavailable ? 'Unavailable' : !connected ? 'Reconnecting…' : 'Live';

  const card = (
    <Tag className={`ov-tile${canOpen ? ' ov-tile--open' : ''} onl-tile`} {...tagProps}>
      <span className="ov-tile-label">
        <Radio size={13} aria-hidden="true" />
        Online trainers
        <span className="onl-tile-status" title={status}>
          {dot}
        </span>
      </span>
      <span className="ov-tile-value">{count}</span>
    </Tag>
  );

  /* Portalled to <body>: a position:fixed veil is measured from the
     nearest transformed ancestor, and the admin overview animates its
     tab body -- same reason OnlineStudentsBox and ExportDialog portal. */
  const popup =
    showList &&
    createPortal(
      <div className="pg-modal" role="presentation" onClick={() => setShowList(false)}>
        <div
          className="pg-modal-panel pg-modal-panel--wide"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="pg-modal-head">
            <div className="pg-modal-heading">
              <span className="pg-modal-eyebrow">Trainers</span>
              <h3 className="pg-modal-title" id={titleId}>
                Online now
              </h3>
              <p className="pg-modal-sub">
                <span className="onl-live">
                  {dot}
                  {trainers.length} online across the institute
                </span>
              </p>
            </div>
            <button
              type="button"
              className="pg-modal-close"
              aria-label="Close"
              onClick={() => setShowList(false)}
            >
              <X size={16} />
            </button>
          </header>

          <div className="pg-modal-body">
            {trainers.length === 0 ? (
              <p className="pgm-empty">Nobody is online right now.</p>
            ) : (
              /* The view popup's table (view_popup_rules): the row is the
                 bridge to the trainer -- click, Enter or Space opens their
                 record through Waypoint, and the popup closes first. */
              <div className="pgm-tablescroll">
                <table className="pgm-table pgm-table--values">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Trainer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainers.map((t, i) => (
                      <tr
                        key={t.key}
                        className="pgm-row--open"
                        tabIndex={0}
                        role="button"
                        aria-label={`Open ${t.name}`}
                        onClick={() => openTrainer(t)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openTrainer(t);
                          }
                        }}
                      >
                        <td className="pgm-num">{i + 1}</td>
                        <td>
                          <span className="pgm-person-id">
                            <span className="pgm-person-name">
                              <span className="onl-live">
                                {dot}
                                {t.name}
                              </span>
                            </span>
                            <span className="pgm-person-mail">
                              {t.specialization || 'No specialization on file'}
                            </span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      {card}
      {popup}
    </>
  );
};

export default OnlineTrainersBox;
